import type { Pin, SearchLocationMode } from "@/api/types";
import { type Bbox, normalizeBbox, normalizeLongitude } from "@/lib/geo-bounds";

const PIN_BBOX_PAD = 0.001;
const COORDS_FOCUS_PAD = 0.01;

function selectPinsForInitialFit(pins: Pin[], searchLocationMode?: SearchLocationMode): Pin[] {
  if (searchLocationMode !== "place") return pins;

  const insidePins = pins.filter((pin) => pin.inside_selected_area === true);
  return insidePins.length > 0 ? insidePins : pins;
}

function computeBboxFromPins(pins: Pin[]): Bbox | null {
  const longitudes: number[] = [];
  let minLat = Infinity;
  let maxLat = -Infinity;

  for (const pin of pins) {
    if (pin.lat == null || pin.lng == null) continue;
    longitudes.push(normalizeLongitude(pin.lng));
    if (pin.lat < minLat) minLat = pin.lat;
    if (pin.lat > maxLat) maxLat = pin.lat;
  }

  longitudes.sort((a, b) => a - b);
  const first = longitudes[0];
  const last = longitudes.at(-1);
  if (first === undefined || last === undefined) return null;
  let west = first;
  let east = last;
  let largestGap = west + 360 - east;
  let previous = first;

  // The shortest enclosing arc excludes the largest gap between pin longitudes.
  for (const current of longitudes) {
    const gap = current - previous;
    if (gap > largestGap) {
      largestGap = gap;
      west = current;
      east = previous + 360;
    }
    previous = current;
  }

  return normalizeBbox([
    west - PIN_BBOX_PAD,
    minLat - PIN_BBOX_PAD,
    east + PIN_BBOX_PAD,
    maxLat + PIN_BBOX_PAD,
  ]);
}

function computeInitialFitBbox(
  pins: Pin[],
  searchLocationMode?: SearchLocationMode,
  searchLat?: number,
  searchLng?: number,
): Bbox | null {
  if (searchLocationMode === "coords" && searchLat != null && searchLng != null) {
    return normalizeBbox([
      searchLng - COORDS_FOCUS_PAD,
      searchLat - COORDS_FOCUS_PAD,
      searchLng + COORDS_FOCUS_PAD,
      searchLat + COORDS_FOCUS_PAD,
    ]);
  }

  const fitPins = selectPinsForInitialFit(pins, searchLocationMode);
  return computeBboxFromPins(fitPins);
}

export { computeBboxFromPins, computeInitialFitBbox, selectPinsForInitialFit };
